// External libraries
import { Box, Flex, useColorModeValue, Skeleton } from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Components
import SubQuestChart from 'components/subQuestReport/SubQuestChart';
import SubQuestDetails from 'components/subQuestReport/SubQuestDetails';
import SubQuestLogs from 'components/subQuestReport/SubQuestLogs';
import SubQuestRecords from 'components/subQuestReport/SubQuestRecords';
import DeleteProgressionMd from 'components/subQuestReport/modals/DeleteProgressionMd';

// Hooks
import useToastNotif from 'hooks/useToastNotif';

// Redux actions
import {
  getSubQuestByIdAction,
  getSubQuestLogsAction,
  getSubQuestProgressionsAction,
  getSubQuestProgressionsCountAction,
} from 'store/modules/subQuestReport/subQuest-report-action';

// Utilities
import {
  baseSubQuestColumns,
  interactiveSubQuestColumns,
  quizSubQuestColumns,
  videoSubQuestColumns,
  narrativeSubQuestColumns,
  fileSubmissionSubQuestColumns,
} from 'utils/columns/reports/progressionColumns';
import { ReducerStatus } from 'utils/constants';
import {
  INTERACTIVE_ID,
  QUIZ_ID,
  VIDEO_ID,
  NARRATIVE_ID,
  FILE_SUBMISSION_ID,
} from 'utils/constants/subQuestType';
import { TOAST_ERROR } from 'utils/constants/toast';
import ProgressionSessionMd from 'components/subQuestReport/modals/ProgressionSessionMd';
import DeleteProgressionSessionMd from 'components/subQuestReport/modals/DeleteProgressionSessionMd';
import UpdateFileSubmissionSessionMd from 'components/subQuest/modals/fileSubmissions/session/UpdateFileSubmissionSessionMd';
import UpdateQuizSessionMd from 'components/subQuestReport/modals/UpdateQuizSessionMd';
import ExportFileSessionsMd from 'components/subQuestReport/modals/ExportFileSessionsMd';
import ImportFileSessionsMd from 'components/subQuestReport/modals/ImportFileSessionsMd';

function QuizProfileReportPage() {
  // Extract the subquest ID from the URL parameters
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const progressionId = searchParams.get('progressionId');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Custom hook for generating toast notifications
  const { generateToastNotif } = useToastNotif();

  // Redux selectors to fetch state data
  const subQuest = useSelector((state) => state.subQuestReport.subQuest);
  const progressions = useSelector(
    (state) => state.subQuestReport.progressions
  );
  const logs = useSelector((state) => state.subQuestReport.logs);
  const progressionsCount = useSelector(
    (state) => state.subQuestReport.progressionsCount
  );
  const progressionsMetaData = useSelector(
    (state) => state.subQuestReport.progressionMetaData
  );

  const getSubQuest = useSelector((state) => state.subQuestReport.getSubQuest);
  const getProgressions = useSelector(
    (state) => state.subQuestReport.getProgressions
  );
  const getLogs = useSelector((state) => state.subQuestReport.getLogs);
  const getProgressionsCount = useSelector(
    (state) => state.subQuestReport.getProgressionsCount
  );

  const updateFileSubmissionSessionState = useSelector(
    (state) => state.subquest.updateFileSubmissionSession
  );

  // Local state variables for managing table and search behavior
  const [columns, setColumns] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('asc');
  const [sortBy, setSortBy] = useState('');
  const [searchText, setSearchText] = useState('');

  // Filter state variables
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  /**
   * Generic handler factory for filter changes
   * @param {function} setter - State setter function
   * @returns {function} - Handler function for filter changes
   */
  const handleFilterChange = (setter) => (selectedOption) => {
    setter(selectedOption);
  };

  const handleUserChange = handleFilterChange(setSelectedUser);
  const handleCourseChange = handleFilterChange(setSelectedCourse);
  const handleQuestChange = handleFilterChange(setSelectedQuest);
  const handleResultChange = handleFilterChange(setSelectedResult);

  // Function to fetch progression data with filters and pagination
  const fetchData = useCallback(
    ({
      selectedPage,
      selectedPageSize,
      selectedOrderBy,
      selectedSortBy,
      selectedQuery,
      userName,
      courseId,
      questId,
      result,
    }) => {
      dispatch(
        getSubQuestProgressionsAction({
          id,
          currentPage: selectedPage || 1,
          pageSize: selectedPageSize || perPage,
          orderBy: selectedOrderBy,
          sortBy: selectedSortBy,
          query: selectedQuery,
          userName,
          courseId,
          questId,
          result,
        })
      );
    },
    [dispatch, id, perPage]
  );

  // Handle changes in the number of rows per page
  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      selectedPage: page,
      selectedPageSize: newPerPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
      userName: selectedUser?.value,
      courseId: selectedCourse?.value,
      questId: selectedQuest?.value,
      result: selectedResult?.value,
    });
  };

  // Handle sorting of table columns
  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      selectedPage: 1,
      selectedPageSize: perPage,
      selectedOrderBy: sortDirection,
      selectedSortBy: column.sortField,
      selectedQuery: searchText,
      userName: selectedUser?.value,
      courseId: selectedCourse?.value,
      questId: selectedQuest?.value,
      result: selectedResult?.value,
    });
  };

  // Handle page changes in the table
  const handlePageChange = (page) => {
    fetchData({
      selectedPage: page,
      selectedPageSize: perPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
      userName: selectedUser?.value,
      courseId: selectedCourse?.value,
      questId: selectedQuest?.value,
      result: selectedResult?.value,
    });
  };

  // Debounced function to handle search input changes
  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          selectedPage: 1,
          selectedPageSize: perPage,
          selectedQuery: query,
          userName: selectedUser?.value,
          courseId: selectedCourse?.value,
          questId: selectedQuest?.value,
          result: selectedResult?.value,
        });
      }, 350),
    [
      fetchData,
      perPage,
      selectedUser,
      selectedCourse,
      selectedQuest,
      selectedResult,
    ]
  );

  // Update search text and trigger debounced fetch
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  // Reload logs data
  const handleReloadLogs = () => {
    dispatch(getSubQuestLogsAction(id));
  };

  // Reload progression counts
  const handleReloadCounts = () => {
    dispatch(getSubQuestProgressionsCountAction(id));
  };

  // Fetch subquest details on component mount or ID change
  useEffect(() => {
    dispatch(getSubQuestByIdAction(id));
  }, [id, dispatch]);

  // Set search text when progressionId is present in URL
  useEffect(() => {
    if (progressionId) {
      setSearchText(progressionId);
    }
  }, [progressionId]);

  // Update table columns based on subquest type
  useEffect(() => {
    if (subQuest != null) {
      document.title = `Skill Quest | ${subQuest.name} Report`;
      switch (subQuest.type.id) {
        case VIDEO_ID:
          setColumns(videoSubQuestColumns);
          break;
        case QUIZ_ID:
          setColumns(quizSubQuestColumns);
          break;
        case INTERACTIVE_ID:
          setColumns(interactiveSubQuestColumns);
          break;
        case NARRATIVE_ID:
          setColumns(narrativeSubQuestColumns);
          break;
        case FILE_SUBMISSION_ID:
          setColumns(fileSubmissionSubQuestColumns);
          break;
        default:
          setColumns(baseSubQuestColumns);
          break;
      }
    }
  }, [subQuest]);

  // Handle errors when fetching subquest details
  useEffect(() => {
    if (getSubQuest.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getSubQuest.error?.data?.status || '404',
        getSubQuest.error?.data?.title || 'Not Found',
        true,
        false
      );
      navigate('/404');
    }
  }, [getSubQuest, navigate, generateToastNotif]);

  // Fetch progression data and logs when subquest details are successfully loaded
  useEffect(() => {
    if (getSubQuest.status === ReducerStatus.SUCCEEDED) {
      dispatch(getSubQuestProgressionsCountAction(id));
      dispatch(getSubQuestLogsAction(id));
      fetchData({
        selectedPage: 1,
        selectedPageSize: perPage,
        selectedQuery: searchText,
        userName: selectedUser?.value,
        courseId: selectedCourse?.value,
        questId: selectedQuest?.value,
        result: selectedResult?.value,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSubQuest.status, id, dispatch, progressionId]);

  /**
   * Re-fetch data when any filter changes
   * Resets to first page when filters are modified
   */
  useEffect(() => {
    if (getSubQuest.status === ReducerStatus.SUCCEEDED) {
      fetchData({
        selectedPage: 1,
        selectedPageSize: perPage,
        selectedQuery: searchText,
        userName: selectedUser?.value,
        courseId: selectedCourse?.value,
        questId: selectedQuest?.value,
        result: selectedResult?.value,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedCourse, selectedQuest, selectedResult]);

  return (
    <Flex
      minH="100vh"
      ml={{ base: 0, md: 20 }}
      direction={{ base: 'column', lg: 'row' }}
    >
      {/* Modal for deleting progressions */}
      <DeleteProgressionMd />

      {/* Modal for viewing progression sessions */}
      <ProgressionSessionMd />

      {/* Modal for deleting progression session */}
      <DeleteProgressionSessionMd />

      {/* Modal for grading file submissions */}
      {updateFileSubmissionSessionState.modal && subQuest && (
        <UpdateFileSubmissionSessionMd />
      )}

      {/* Modal for updating quiz session */}
      <UpdateQuizSessionMd />

      {/* Export Submission Sessions Modal */}
      <ExportFileSessionsMd />

      {/* Import Submission Sessions Modal */}
      <ImportFileSessionsMd />

      {/* Main content area */}
      <Box flex="1" h="100%" p={5}>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={2} wrap="wrap">
          {/* Subquest details section */}
          <SubQuestDetails
            getStatus={getSubQuest.status}
            subQuest={subQuest}
            flex={1}
          />

          {/* Progression chart section */}
          <Skeleton
            isLoaded={getProgressionsCount.status === ReducerStatus.SUCCEEDED}
          >
            <SubQuestChart
              status={getProgressionsCount.status}
              pendingCount={progressionsCount?.pending}
              completedCount={progressionsCount?.completed}
              handleReloadData={handleReloadCounts}
              w={{ base: '100%', lg: '350px' }}
              h="100%"
            />
          </Skeleton>
        </Flex>

        {/* User progression records table */}
        <SubQuestRecords
          mt={5}
          w="100%"
          subQuest={subQuest}
          status={getProgressions.status}
          data={progressions}
          columns={columns}
          totalItems={progressionsMetaData?.totalItems}
          searchText={searchText}
          selectedUser={selectedUser}
          selectedCourse={selectedCourse}
          selectedQuest={selectedQuest}
          selectedResult={selectedResult}
          handleUserChange={handleUserChange}
          handleCourseChange={handleCourseChange}
          handleQuestChange={handleQuestChange}
          handleResultChange={handleResultChange}
          handlePerRowsChange={handlePerRowsChange}
          handleSort={handleSort}
          handlePageChange={handlePageChange}
          handleSearchTextChange={handleSearchTextChange}
          handleReloadData={fetchData}
        />
      </Box>

      {/* Logs section */}
      <Box w={{ base: '100%', lg: '250px' }} p={{ base: 5, lg: 0 }}>
        <SubQuestLogs
          logs={logs}
          status={getLogs.status}
          handleReloadData={handleReloadLogs}
          bg={useColorModeValue('white', 'gray.800')}
          color={useColorModeValue('gray.600', 'white')}
          id="subquest-directory-container"
          className={useColorModeValue('scrollbar-light', 'scrollbar-dark')}
          overflowY="scroll"
          h="100vh"
          borderLeftWidth="1px"
          p={2}
        />
      </Box>
    </Flex>
  );
}

export default QuizProfileReportPage;
